/* src/knotsInscriber.jsx */
import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import useKnotsInscribe from "./useKnotsInscribe";
import KnotsGalleryAdaptive from "./KnotsGalleryAdaptive";

/* ───── endpoints ───── */
const ORD_API = "https://ord.natcats.xyz/api";
const ORCH_API = "https://knots.natcats.xyz/api";

/* ───── easy UI toggles (requested) ───── */
/**
 * OFF by default (requested).
 * When TRUE:
 * - navbar + sticky gallery toolbar backgrounds become transparent
 * - navbar becomes fixed-overlay so scroll content can be visible beneath it
 */
const TRANSPARENT_NAV_AND_TOOLBAR = false;

/**
 * When TRUE:
 * - force all UI text to orange (keeps layout, just color)
 */
const FORCE_ORANGE_TEXT = false;

/* reservation window (match backend LOCK_TTL) */
const CLAIM_TTL = 300; // 5 minutes

/* toggle to re-enable the “Deliver to” field later without changing code */
const SHOW_RECIPIENT = false;

/* theme tuner toggle (keep code, disable in UI) */
const ENABLE_TUNER = false;

/* temporary toggle */
const SHOW_MINT_INFO_ICON = false;
const ENABLE_LAUNCH_GALLERY_MODE_TOGGLE = false;

/* ✅ PAUSE TOGGLE (front-end only) */
const INSCRIBE_PAUSED = false;

/* ✅ UI toggles: keep functionality in code, remove from UI */
const SHOW_WALLET_UI = false; // UniSat connect / wallet display UI removed
const PUBLIC_INSCRIBE_ENABLED = false; // clicking CTA does NOT open form; shows "mint complete"

/**
 * ✅ INTERNAL ACCESS VIA URL QUERY PARAMS
 * - ?inscribe=1  -> allows opening/using the inscribe form even while paused
 * - ?openInscribe=1 -> auto-opens the form on page load (requires ?inscribe=1 if paused)
 *
 * Example:
 *   /?inscribe=1&openInscribe=1
 */
const INTERNAL_INSCRIBE_PARAM = "inscribe";
const INTERNAL_OPEN_PARAM = "openInscribe";

/* public assets */
const SUBTEXT_SRC = "/knots-subtext.svg";
const DL_ICON_SRC = "/download.svg";
const INFO_ICON_SRC = "/Info%20outline.svg";

/* layout constants */
const FORM_AREA_MIN_HEIGHT = 440;

/**
 * ✅ 100 assets per page (cap)
 * - Used for list/history fetches in this file.
 * - Also passed through to KnotsGalleryAdaptive (it can ignore if unused).
 */
const LIST_PAGE_SIZE = 100;
const ASSETS_PAGE_SIZE = 100;

/* ───── helpers ───── */
const fetchJson = async (url, opts = {}) => {
  const r = await fetch(url, opts);
  const t = await r.text();
  let j;
  try {
    j = JSON.parse(t || "{}");
  } catch {
    j = {};
  }
  if (!r.ok) {
    const e = new Error(j.error || t || `HTTP ${r.status}`);
    e.status = r.status;
    throw e;
  }
  return j;
};

/* BTC/USD (quiet) */
function useUsdRate() {
  const [usd, setUsd] = useState(null);
  useEffect(() => {
    let alive = true;
    const get = async () => {
      try {
        const j = await fetch(
          "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd"
        ).then((r) => r.json());
        if (alive) setUsd(j?.bitcoin?.usd ?? null);
      } catch {}
    };
    get();
    const id = setInterval(get, 300_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);
  return usd;
}

function useWindowWidth() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const f = () => setW(window.innerWidth);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return w;
}

/* sorting + labels (shared by list) */
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
};

const cleanTxid = (tx) => (tx ? String(tx).replace(/i0$/i, "") : "");

const parseKnotFromText = (txt) => {
  if (!txt) return NaN;
  const m = String(txt).match(/(?:^|\b)knot\s*(\d+)/i);
  if (m) return num(m[1]);
  const any = String(txt).match(/(\d{1,6})/);
  return any ? num(any[1]) : NaN;
};

const getKnotNoNumeric = (it) => {
  const direct = num(it?.knot_no ?? it?.knotNo ?? it?.knot_number ?? it?.knotNumber);
  if (Number.isFinite(direct)) return direct;
  const byName = parseKnotFromText(it?.knot_name ?? it?.name);
  if (Number.isFinite(byName)) return byName;
  const byAsset = parseKnotFromText(it?.asset_id);
  if (Number.isFinite(byAsset)) return byAsset;
  return NaN;
};

const tsCompleted = (it) => num(it?.completed_ts ?? it?.completedTs ?? 0) || 0;
const tsReserved = (it) => num(it?.reserved_ts ?? it?.reservedTs ?? 0) || 0;

const sortByKnotDescThenTime = (a, b) => {
  const ak = getKnotNoNumeric(a),
    bk = getKnotNoNumeric(b);
  const aUn = Number.isNaN(ak),
    bUn = Number.isNaN(bk);
  if (aUn && !bUn) return 1;
  if (!aUn && bUn) return -1;
  if (!aUn && !bUn && ak !== bk) return bk - ak;
  const at = tsCompleted(a) || tsReserved(a) || 0;
  const bt = tsCompleted(b) || tsReserved(b) || 0;
  if (at !== bt) return at - bt;
  return String(a?.asset_id || "").localeCompare(String(b?.asset_id || ""));
};

const ordLink = (tx) => `https://ordinals.com/inscription/${tx}i0`;

/* theme helpers */
const parseHex = (h) => {
  const s = (h || "").replace("#", "").trim();
  if (s.length === 3) {
    const r = s[0] + s[0],
      g = s[1] + s[1],
      b = s[2] + s[2];
    return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)];
  }
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
};

const isLightHex = (h) => {
  const [r, g, b] = parseHex(h);
  const [R, G, B] = [r, g, b].map((v) => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  return L > 0.5;
};

/* base blocks */
const contentNarrow = { maxWidth: 420, margin: "0 auto" };
const baseMasthead = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
};
const baseMastCenter = {
  flex: "0 0 auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
};
const baseMastNarrowTop = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 10,
};
const baseMastLogoNarrow = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  marginTop: 10,
  marginBottom: 16,
};
const walletRow = { display: "flex", alignItems: "center", gap: 10 };
const inscribeCenterWrap = {
  minHeight: "calc(100vh - 120px)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  paddingTop: 8,
  paddingBottom: 24,
};

/* style factory */
function makeStyles(theme, sideWidthPx = 200, navHeightPx = 0) {
  const ORANGE = "#FF3300";

  const light = isLightHex(theme.bg);
  const forcedText = FORCE_ORANGE_TEXT ? ORANGE : null;

  const textMain = forcedText || theme.textA || "#111111";
  const textAlt = forcedText || theme.textB || textMain;
  const cardText = forcedText || theme.cardText || "#555555";
  const buttonText = forcedText || theme.buttonText || "#FFFFFF";
  const ctaColor = forcedText || theme.cta || ORANGE;

  const placeholderFlat = "rgba(0,0,0,0.06)";
  const isOrange = theme.mode === "orange";

  const barBg = TRANSPARENT_NAV_AND_TOOLBAR ? "transparent" : theme.bg;
  const lightboxTextColor = forcedText || "#FFFFFF";

  // when navbar is overlay/fixed, sticky gallery controls should sit below it
  const stickyTop = TRANSPARENT_NAV_AND_TOOLBAR ? Math.max(0, Number(navHeightPx) || 0) : 0;

  return {
    // exposed colors for components
    lightboxTextColor,

    page: {
      height: "100vh",
      display: "flex",
      flexDirection: "column",
      background: theme.bg,
      color: textMain,
      padding: "12px 0 0 16px",
      fontFamily:
        "'Arimo', system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif",
      boxSizing: "border-box",
      userSelect: "none",
    },

    headerOuter: {
      background: barBg,
      paddingRight: 16,
      ...(TRANSPARENT_NAV_AND_TOOLBAR
        ? {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 12,
            boxSizing: "border-box",
          }
        : null),
    },

    mainScroll: { flex: 1, overflowY: "auto" },

    masthead: { ...baseMasthead, background: barBg },
    mastSide: {
      flex: `0 0 ${sideWidthPx}px`,
      display: "flex",
      alignItems: "flex-start",
    },
    mastSideRight: {
      flex: `0 0 ${sideWidthPx}px`,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-end",
      gap: 10,
    },
    mastCenter: baseMastCenter,
    mastheadNarrowTop: { ...baseMastNarrowTop, background: barBg },
    mastLogoNarrow: { ...baseMastLogoNarrow, background: barBg },

    logoWrap: {
      display: "block",
      width: "100%",
      maxWidth: Math.round(560 * (theme.titleScale ?? 0.66)),
    },

    // ✅ heavier (match CTA weight)
    microLink: { color: textMain, fontSize: 14, fontWeight: 600, opacity: 0.95 },

    headerLink: {
      background: "none",
      border: "none",
      color: textMain,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
      padding: 0,
      textDecoration: "underline",
      outline: "none",
      whiteSpace: "nowrap",
    },

    ctaLink: {
      background: "none",
      border: "none",
      color: ctaColor,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
      padding: 0,
      textDecoration: "underline",
      outline: "none",
      whiteSpace: "nowrap",
    },

    iconGhostXL: {
      background: "transparent",
      border: "none",
      color: textMain,
      width: 40,
      height: 40,
      lineHeight: "38px",
      textAlign: "center",
      borderRadius: 0,
      cursor: "pointer",
      marginLeft: 8,
      fontSize: 28,
    },

    navXBtn: {
      background: "transparent",
      border: "none",
      color: textMain,
      cursor: "pointer",
      fontSize: 20,
      lineHeight: 1,
      padding: 0,
      margin: 0,
    },

    toolbarLink: {
      background: "none",
      border: "none",
      color: textMain,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
      padding: 0,
      textDecoration: "underline",
      outline: "none",
      fontFamily:
        "'Arimo', system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif",
    },
    toolbarLinkActive: {
      background: "none",
      border: "none",
      color: textMain,
      cursor: "default",
      fontSize: 14,
      fontWeight: 600,
      padding: 0,
      textDecoration: "none",
      outline: "none",
      fontFamily:
        "'Arimo', system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif",
    },

    headerToolbarBar: { padding: "4px 0 8px" },

    sectionHead: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
    },

    galleryControlsBar: {
      position: "sticky",
      top: stickyTop, // ✅ offset when navbar overlay is on
      zIndex: 19,
      background: barBg,
      padding: "4px 0 12px",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap",
    },

    btnPrimary: {
      display: "block",
      width: "100%",
      marginTop: 12,
      padding: "10px 20px",
      background: theme.button,
      color: buttonText,
      border: "none",
      borderRadius: 0,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
    },

    btnPrimaryDisabled: {
      display: "block",
      width: "100%",
      marginTop: 12,
      padding: "10px 20px",
      background: theme.button,
      color: buttonText,
      border: "none",
      borderRadius: 0,
      opacity: 0.6,
      cursor: "default",
      fontSize: 14,
      fontWeight: 600,
    },

    btnSmall: {
      padding: "8px 14px",
      background: theme.button,
      color: buttonText,
      border: "none",
      borderRadius: 0,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
    },

    btnSmallDisabled: {
      padding: "8px 14px",
      background: theme.button,
      color: buttonText,
      border: "none",
      borderRadius: 0,
      opacity: 0.6,
      cursor: "not-allowed",
      fontSize: 14,
      fontWeight: 600,
    },

    btnSmallGhost: {
      padding: "8px 14px",
      background: "transparent",
      color: textMain,
      border: `1px solid ${textMain}`,
      borderRadius: 0,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
    },

    noteNeutral: {
      color: textAlt,
      fontSize: 12,
      fontWeight: 400,
      marginTop: 8,
      opacity: 0.9,
    },

    errLight: {
      color: forcedText || (light ? "#B00000" : "#ffd4d4"),
      fontSize: 12,
      fontWeight: 400,
      marginTop: 10,
    },

    successCard: {
      marginTop: 26,
      padding: 20,
      background: light ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.12)",
      borderRadius: 0,
      minHeight: 0,
    },

    pcBox: {
      border: "none",
      borderRadius: 0,
      padding: "6px 0",
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.3,
      marginBottom: 8,
      background: "transparent",
      color: textMain,
    },

    pcHr: { height: 1, background: "#000000", margin: "8px 0", border: "none" },

    sectionWrap: { width: "100%", padding: "0 0 32px" },

    tileImgBox: {
      width: "100%",
      aspectRatio: "1 / 1",
      background: "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border:
        theme.strokeWeight > 0
          ? `${theme.strokeWeight}px solid ${theme.strokeColor}`
          : "none",
      boxSizing: "border-box",
    },

    imgPlaceholder: { width: "100%", height: "100%", background: placeholderFlat },
    metaText: { color: cardText },

    sizeLink: {
      background: "none",
      border: "none",
      color: textMain,
      opacity: 0.9,
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
      padding: 0,
      textDecoration: "underline",
      fontFamily:
        "'Arimo', system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif",
    },

    sizeLinkActive: {
      background: "none",
      border: "none",
      color: textMain,
      opacity: 1,
      cursor: "default",
      fontSize: 14,
      fontWeight: 600,
      padding: 0,
      textDecoration: "none",
      fontFamily:
        "'Arimo', system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif",
    },

    btnSquare: {
      padding: 0,
      background: "transparent",
      color: textMain,
      border: "none",
      cursor: "pointer",
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 1.0,
      position: "relative",
      top: -2,
    },

    btnSquareDisabled: {
      padding: 0,
      background: "transparent",
      color: textMain,
      border: "none",
      cursor: "not-allowed",
      fontSize: 18,
      fontWeight: 700,
      opacity: 0.4,
      lineHeight: 1.0,
      position: "relative",
      top: -2,
    },

    // index table (kept for later)
    tableRowHeader: {
      display: "grid",
      gridTemplateColumns: "120px 1fr",
      gap: 8,
      padding: "8px 0",
      borderBottom: isOrange
        ? "1px solid rgba(0,0,0,0.35)"
        : light
        ? "1px solid rgba(0,0,0,0.6)"
        : "1px solid rgba(255,255,255,0.4)",
    },

    tableRow: {
      display: "grid",
      gridTemplateColumns: "120px 1fr",
      gap: 8,
      padding: "8px 0",
      borderBottom: isOrange
        ? "1px solid rgba(0,0,0,0.35)"
        : light
        ? "1px solid rgba(0,0,0,0.6)"
        : "1px solid rgba(255,255,255,0.4)",
    },

    cornerHotArea: { position: "fixed", width: 48, height: 48, zIndex: 9997 },

    altToggleBtn: {
      background: light ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.9)",
      color: light ? "#fff" : "#111",
      border: "none",
      borderRadius: 16,
      padding: "6px 10px",
      cursor: "pointer",
      fontSize: 12,
      outline: "none",
    },

    tunerBtn: {
      background: light ? "rgba(0,0,0,0.7)" : "rgba(255,255,255,0.9)",
      color: light ? "#fff" : "#111",
      border: "none",
      borderRadius: 16,
      width: 32,
      height: 32,
      cursor: "pointer",
      fontSize: 18,
      lineHeight: "32px",
      outline: "none",
    },

    tunerPanel: {
      marginTop: 6,
      padding: 8,
      width: 260,
      background: light ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.85)",
      color: light ? "#111" : "#fff",
      border: `1px solid ${
        light ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.25)"
      }`,
      fontSize: 11,
      position: "fixed",
      right: 10,
      bottom: 52,
      zIndex: 9999,
    },

    tunerRow: {
      display: "grid",
      gridTemplateColumns: "120px 1fr",
      alignItems: "center",
      gap: 6,
      marginBottom: 6,
    },

    tunerRow2: {
      display: "grid",
      gridTemplateColumns: "120px 1fr 1fr",
      alignItems: "center",
      gap: 6,
      marginBottom: 6,
    },

    tunerLabel: { opacity: 0.85 },

    tunerInputColor: {
      width: "100%",
      height: 22,
      padding: 0,
      border: "1px solid rgba(0,0,0,0.15)",
      outline: "none",
    },

    tunerInputNum: {
      width: "100%",
      height: 22,
      border: "1px solid rgba(0,0,0,0.15)",
      padding: "0 4px",
      outline: "none",
    },

    tunerFooter: { display: "flex", justifyContent: "space-between", gap: 8, marginTop: 6 },

    tunerLink: {
      background: "transparent",
      border: "1px solid",
      borderColor: light ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.4)",
      color: light ? "#111" : "#fff",
      cursor: "pointer",
      padding: "4px 6px",
      fontSize: 11,
      outline: "none",
    },

    // refresh text link for quote
    refreshLink: {
      background: "none",
      border: "none",
      color: textMain,
      cursor: "pointer",
      fontSize: 12,
      fontWeight: 600,
      padding: 0,
      textDecoration: "underline",
      outline: "none",
    },

    // ✅ help accordion (UNDER Sign & Submit)
    helpAccWrap: { paddingTop: 20, color: textMain },

    helpAccHeader: {
      width: "100%",
      background: "transparent",
      border: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      cursor: "pointer",
      color: textMain,
      fontSize: 14,
      fontWeight: 600,
      textDecoration: "underline",
      outline: "none",
      textAlign: "left",
      fontFamily:
        "'Arimo', system-ui, -apple-system, Segoe UI, Roboto, Inter, sans-serif",
    },

    helpAccChevron: {
      fontSize: 14,
      fontWeight: 700,
      lineHeight: 1,
      marginLeft: 10,
      opacity: 0.95,
      color: textMain,
    },

    helpAccBody: {
      marginTop: 10,
      background: "transparent",
      border: "none",
      padding: 0,
      color: textMain,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.45,
      textAlign: "left",
    },

    helpAccItem: {
      margin: "0 0 8px",
      color: textMain,
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.45,
    },
    helpAccLink: {
      color: textMain,
      textDecoration: "underline",
      fontWeight: 400,
      outline: "none",
    },

    lightboxBackdrop: (bg) => ({
      position: "fixed",
      inset: 0,
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9998,
    }),

    lightboxCenter: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      maxWidth: "92vw",
      maxHeight: "92vh",
    },

    lightboxInlineWrap: { display: "inline-block", maxWidth: "92vw" },

    lightboxImageWrap: {
      width: "auto",
      maxHeight: "80vh",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
    },

    lightboxImg: (pixelate) => ({
      maxHeight: "80vh",
      maxWidth: "92vw",
      width: "auto",
      height: "auto",
      objectFit: "contain",
      imageRendering: pixelate ? "pixelated" : "auto",
      display: "block",
      userSelect: "none",
    }),

    lightboxCaption: {
      marginTop: 8,
      textAlign: "left",
      fontSize: 12,
      fontWeight: 700,
      color: lightboxTextColor,
    },

    lightboxClose: {
      position: "fixed",
      top: 14,
      right: 16,
      border: "none",
      background: "transparent",
      color: lightboxTextColor,
      cursor: "pointer",
      fontSize: 26,
      fontWeight: 400,
      lineHeight: 1,
      padding: 4,
    },
  };
}

/* Title */
function TitleText({ styles, color, onClick }) {
  return (
    <div style={styles.logoWrap}>
      <button
        type="button"
        onClick={onClick}
        style={{
          display: "block",
          width: "100%",
          fontSize: 20,
          fontWeight: 700,
          color,
          textAlign: "center",
          lineHeight: 1.2,
          userSelect: "none",
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          cursor: onClick ? "pointer" : "default",
        }}
      >
        Bitcoin Knots
      </button>
    </div>
  );
}

/* hover tooltip for Mint fee (icon-based) */
function InfoIcon({ text }) {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
      }}
    >
      <img
        src={INFO_ICON_SRC}
        alt=""
        draggable={false}
        style={{ width: 16, height: 16, display: "block", userSelect: "none" }}
      />
      {hover && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 8,
            width: 260,
            padding: "10px 12px",
            background: "#000000",
            color: "#FFFFFF",
            borderRadius: 8,
            fontSize: 11,
            fontWeight: 600,
            lineHeight: 1.35,
            boxShadow: "0 6px 14px rgba(0,0,0,0.28)",
            whiteSpace: "pre-line",
            pointerEvents: "none",
            zIndex: 50,
            textAlign: "left",
          }}
        >
          {text}
        </div>
      )}
    </span>
  );
}

/* price panels */
function PriceCard({ mint, network, usdRate, styles }) {
  const total = (mint || 0) + (network || 0);
  const fmt = (n) => Number(n || 0).toLocaleString();
  const usd = (n) => (usdRate ? `($${(n * 1e-8 * usdRate).toFixed(2)})` : "");
  const tooltipText = "mint starts at 25,000 sats for this phase\nand increases incrementally";

  const Row = (lab, val, showInfo = false) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <span style={{ fontSize: 14, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
        {lab}
        {showInfo && SHOW_MINT_INFO_ICON && <InfoIcon text={tooltipText} />}
      </span>
      <span
        style={{
          display: "inline-block",
          minWidth: 220,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        {fmt(val)} sats{usd(val)}
      </span>
    </div>
  );

  return (
    <div style={styles.pcBox}>
      {Row("Mint fee", mint || 0, true)}
      <div style={styles.pcHr} />
      {Row("Network fee", network || 0)}
      <div style={styles.pcHr} />
      {Row("Total", total)}
    </div>
  );
}

function StablePlaceholderPriceCard({ styles }) {
  const Row = (lab) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{lab}</span>
      <span
        style={{
          display: "inline-block",
          minWidth: 220,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
          opacity: 0.7,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        — sats
      </span>
    </div>
  );

  return (
    <div style={styles.pcBox}>
      {Row("Mint fee")}
      <div style={styles.pcHr} />
      {Row("Network fee")}
      <div style={styles.pcHr} />
      {Row("Total")}
    </div>
  );
}

/* ✅ Help accordion (UNDER Sign & Submit) */
function HelpAccordionUnderSubmit({ styles }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.helpAccWrap}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={styles.helpAccHeader} aria-expanded={open}>
        <span>Need help?</span>
        <span style={styles.helpAccChevron} aria-hidden="true">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <div style={styles.helpAccBody}>
          <div style={styles.helpAccItem}>* Broadcast stuck? Try Refresh to re-fetch the quote and retry the submit.</div>
          <div style={styles.helpAccItem}>
            * “No large enough UTXO” error? Your funds may be split into small UTXOs. Consolidate by sending BTC to your
            own address once (this creates a larger spendable UTXO).
          </div>
          <div style={styles.helpAccItem}>
            * UTXO busy / pending: If a UTXO is already used in a pending transaction, it usually can’t be reused until
            that transaction confirms.
          </div>
          <div style={{ ...styles.helpAccItem, marginBottom: 0 }}>
            * Still stuck? Ping ev in Discord:{" "}
            <a href="https://discord.gg/PaQPwWXUSz" target="_blank" rel="noreferrer" style={styles.helpAccLink}>
              https://discord.gg/PaQPwWXUSz
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

/* Index / list (kept for later; UI removed) */
function KnotsList({ usdRate, styles }) {
  void usdRate;

  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const sentinelRef = useRef(null);
  const seenRef = useRef(new Set());
  const scrollFallbackRef = useRef(null);

  const itemKey = (it) => `${cleanTxid(it.reveal_txid || "")}|${it.asset_id || ""}`;

  const fetchPage = async (cur = null) => {
    let url = `${ORCH_API}/history?limit=${LIST_PAGE_SIZE}&fields=asset_id,knot_no,knot_name,reveal_txid,platform_fee_sats,status,completed_ts,reserved_ts`;
    if (cur) url += `&cursor=${encodeURIComponent(cur)}`;
    const res = await fetchJson(url);
    let items = Array.isArray(res) ? res : res.items || res.data || res.history || [];
    let next =
      (!Array.isArray(res) && (res.next || res.next_cursor || res.cursor || res.last_evaluated_key)) || null;

    items = (items || []).filter(
      (it) => (it.status === "done" || it.status === "completed") && it.asset_id && it.reveal_txid
    );

    return { items, next };
  };

  const loadMore = async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    try {
      const { items, next } = await fetchPage(cursor);
      const fresh = [];
      for (const it of items || []) {
        const k = itemKey(it);
        if (!seenRef.current.has(k)) {
          seenRef.current.add(k);
          fresh.push(it);
        }
      }
      setRows((prev) => {
        const merged = [...prev, ...fresh];
        merged.sort(sortByKnotDescThenTime);
        return merged;
      });
      setCursor(next);
      setHasMore(Boolean(next) && fresh.length > 0);
      if (!initialLoaded) setInitialLoaded(true);
    } catch (e) {
      setErr(e.message || String(e));
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialLoaded || !sentinelRef.current) return;
    let io = null;
    const onIntersect = (entries) => {
      if (entries[0].isIntersecting) loadMore();
    };
    try {
      io = new IntersectionObserver(onIntersect);
      io.observe(sentinelRef.current);
    } catch {
      const onScroll = () => {
        if (!hasMore || loading) return;
        const rect = sentinelRef.current.getBoundingClientRect();
        if (rect.top <= window.innerHeight + 200) loadMore();
      };
      scrollFallbackRef.current = onScroll;
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    }
    return () => {
      if (io) io.disconnect();
      if (scrollFallbackRef.current) {
        window.removeEventListener("scroll", scrollFallbackRef.current);
        scrollFallbackRef.current = null;
      }
    };
  }, [initialLoaded, cursor, hasMore, loading]);

  return (
    <div style={{ maxWidth: "100%" }}>
      {err && <p style={styles.errLight}>{err}</p>}
      <div style={{ width: "100%", paddingTop: 10, paddingRight: 16, boxSizing: "border-box" }}>
        <div style={styles.tableRowHeader}>
          <div style={{ fontSize: 13, textAlign: "left", fontWeight: 600 }}>Knot</div>
          <div style={{ fontSize: 13, textAlign: "left", fontWeight: 600 }}>Inscription ID</div>
        </div>
        {rows.map((it) => {
          const n = getKnotNoNumeric(it);
          const txBase = cleanTxid(it.reveal_txid);
          const idText = txBase ? `${txBase}i0` : "—";
          return (
            <div key={`${txBase || it.asset_id}-row`} style={styles.tableRow}>
              <div style={{ fontSize: 13, textAlign: "left", fontWeight: 500 }}>
                {Number.isFinite(n) ? `Knot ${n}` : "—"}
              </div>
              <div
                style={{
                  fontSize: 13,
                  textAlign: "left",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  fontWeight: 500,
                }}
              >
                {txBase ? (
                  <a
                    href={ordLink(txBase)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "inherit", textDecoration: "underline", outline: "none" }}
                    onMouseDown={(e) => e.preventDefault()}
                    onFocus={(e) => e.target.blur()}
                  >
                    {idText}
                  </a>
                ) : (
                  "—"
                )}
              </div>
            </div>
          );
        })}
        <div ref={sentinelRef} style={{ height: 1 }} />
        {loading && <div style={{ padding: "8px 0", fontSize: 12, opacity: 0.9 }}>Loading…</div>}
      </div>
    </div>
  );
}

/* About tab content (kept in code, not shown in UI right now) */
function KnotsAbout() {
  return (
    <div style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}>
      <div
        style={{
          paddingTop: 40,
          paddingRight: 16,
          paddingBottom: 40,
          marginLeft: 12,
          maxWidth: 460,
          fontSize: 16,
          lineHeight: 1.5,
          textAlign: "left",
          fontWeight: 600,
        }}
      >
        <p style={{ margin: "0 0 12px" }}>Bitcoin Knots is an art collection on Bitcoin Ordinals.</p>
        <p style={{ margin: "0 0 12px" }}>
          The collection explores censorship vs. freedom in decentralized systems, and market dynamics as social
          sculpture.
        </p>
        <p style={{ margin: "0 0 12px" }}>To participate you must do one of the following:</p>
        <ol style={{ margin: "0 0 12px 18px", padding: 0, listStyleType: "decimal" }}>
          <li>hold knots forever</li>
          <li>sell knots for as much as humanly possible</li>
          <li>censor knots and send them to filter hell</li>
        </ol>
        <p style={{ margin: "0 0 12px" }}>The choice is yours :)</p>
        <p style={{ margin: "0 0 12px" }}>
          Artworks are intended for viewing in low resolution (no smoothing) when displayed at 336px or larger.
        </p>
        <p style={{ margin: "0 0 0" }}>I haven't checked if the handle for Bitcoin Knots is available.</p>
      </div>
    </div>
  );
}

/* ───── Main ───── */
export default function KnotsInscriber() {
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Arimo:wght@400;600;700&display=swap";
    document.head.appendChild(link);

    const style = document.createElement("style");
    style.innerHTML = `
      @keyframes shimmer { 0% { background-position: 0% 0; } 100% { background-position: 200% 0; } }
      @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
      body { user-select: none; }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const ORANGE = "#FF3300";

  // Default theme preset (kept)
  const [theme, setTheme] = useState({
    mode: "white",
    bg: "#FFFFFF",
    textA: "#111111",
    textB: "#111111",
    cardText: "#111111",
    button: "#111111",
    buttonText: "#FFFFFF",
    strokeColor: "#E6E6E6",
    strokeWeight: 0,
    showSubtext: false,
    titleColor: "#111111",
    titleScale: 0.66,
    cta: "#FF3300",
    ctaInHeader: true,
  });

  const applyThemePreset = (preset) => {
    if (preset === "white") {
      setTheme({
        mode: "white",
        bg: "#FFFFFF",
        textA: "#111111",
        textB: "#111111",
        cardText: "#111111",
        button: "#111111",
        buttonText: "#FFFFFF",
        strokeColor: "#E6E6E6",
        strokeWeight: 0,
        showSubtext: false,
        titleColor: "#111111",
        titleScale: 0.66,
        cta: "#FF3300",
        ctaInHeader: true,
      });
    } else {
      setTheme({
        mode: "orange",
        bg: "#FF3300",
        textA: "#111111",
        textB: "#111111",
        cardText: "#111111",
        button: "#111111",
        buttonText: "#FFFFFF",
        strokeColor: "#E6E6E6",
        strokeWeight: 0,
        showSubtext: false,
        titleColor: "#111111",
        titleScale: 0.66,
        cta: "#FFFFFF",
        ctaInHeader: true,
      });
    }
  };

  const [launchGalleryMode] = useState(false);

  const width = useWindowWidth();
  const sideWidth = width >= 1100 ? 200 : width >= 980 ? 180 : width >= 860 ? 160 : 150;

  // ✅ measure navbar height so (when overlay mode is enabled) scroll content can appear behind it
  const headerRef = useRef(null);
  const [navHeight, setNavHeight] = useState(0);

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const measure = () => {
      const h = Math.ceil(el.getBoundingClientRect().height || 0);
      setNavHeight(h);
    };

    measure();

    let ro = null;
    try {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    } catch {}

    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const styles = makeStyles(theme, sideWidth, navHeight);

  const textMainColor =
    (styles?.page && styles.page.color) || (FORCE_ORANGE_TEXT ? ORANGE : "#111111");

  const connectLabel = width < 700 ? "Connect UniSat" : "Connect UniSat Wallet";
  const usdRate = useUsdRate();

  // Preview mode disabled (ignore URL flags)
  const previewMode = false;

  // ✅ CTA state: click -> "Mint Complete" (3s then reset)
  const [mintCompleteClicked, setMintCompleteClicked] = useState(false);
  const mintCompleteTimerRef = useRef(null);

  useEffect(() => {
    if (!mintCompleteClicked) return;

    if (mintCompleteTimerRef.current) {
      clearTimeout(mintCompleteTimerRef.current);
      mintCompleteTimerRef.current = null;
    }

    mintCompleteTimerRef.current = setTimeout(() => {
      setMintCompleteClicked(false);
      mintCompleteTimerRef.current = null;
    }, 3000);

    return () => {
      if (mintCompleteTimerRef.current) {
        clearTimeout(mintCompleteTimerRef.current);
        mintCompleteTimerRef.current = null;
      }
    };
  }, [mintCompleteClicked]);

  // ✅ Scroll container ref
  const mainScrollRef = useRef(null);
  const scrollMainToTop = (behavior = "auto") => {
    const el = mainScrollRef.current;
    if (!el) return;
    try {
      el.scrollTo({ top: 0, behavior });
    } catch {
      el.scrollTop = 0;
    }
  };

  // ✅ short-lived suppression window to prevent accidental scroll-to-top from any page-change side effects
  const suppressGalleryScrollUntilRef = useRef(0);

  const [showAllAssets, setShowAllAssets] = useState(false);
  const [sizeMode, setSizeMode] = useState("large");
  const [smoothingMode, setSmoothingMode] = useState("auto");
  const [showAltCorner, setShowAltCorner] = useState(false);
  const [showTuneCorner, setShowTuneCorner] = useState(false);
  const [tunerOpen, setTunerOpen] = useState(false);

  // ✅ internal flags from URL
  const [internalInscribeEnabled, setInternalInscribeEnabled] = useState(false);
  const [internalAutoOpen, setInternalAutoOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search || "");
    const allow = sp.get(INTERNAL_INSCRIBE_PARAM) === "1";
    const autoOpen = sp.get(INTERNAL_OPEN_PARAM) === "1";
    setInternalInscribeEnabled(allow);
    setInternalAutoOpen(autoOpen);
  }, []);

  const {
    mode,
    setMode,
    info,
    walletBusy,
    walletErr,
    waitingForWallet,
    knotsErr,
    noStock,
    retrySec,
    handleRetry,
    formOpen,
    timerText,
    feeRate,
    setFeeRate,
    recipient,
    setRecipient,
    quote,
    quoteErr,
    actionErr,
    busyStep,
    quoting,
    revealTxid,
    submitDisabled,
    ensureConnected,
    disconnect,
    startInscribe,
    handleInscribe,
    handleClose,
    handleInscribeAnother,
    refreshQuote,
  } = useKnotsInscribe({
    ORD_API,
    ORCH_API,
    CLAIM_TTL,
    SHOW_RECIPIENT,
  });

  // keep these around (future)
  void recipient;
  void setRecipient;
  void handleInscribeAnother;
  void ENABLE_LAUNCH_GALLERY_MODE_TOGGLE;
  void SUBTEXT_SRC;
  void SHOW_WALLET_UI;
  void applyThemePreset;
  void KnotsList;
  void KnotsAbout;
  void sizeMode;
  void setSizeMode;
  void smoothingMode;
  void setSmoothingMode;

  const isGallery = mode === "gallery";
  const isFormReady = formOpen;

  // ✅ Effective pause: allow internal bypass via ?inscribe=1
  const effectivePaused = INSCRIBE_PAUSED && !internalInscribeEnabled;

  // ✅ prevent public URL param opening when public mint disabled
  const canOpenInscribeForm = PUBLIC_INSCRIBE_ENABLED || internalInscribeEnabled;

  // ✅ One-time auto-open (only if allowed)
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!internalAutoOpen) return;
    if (!canOpenInscribeForm) return;
    if (effectivePaused) return;

    autoOpenedRef.current = true;
    startInscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalAutoOpen, canOpenInscribeForm, effectivePaused, startInscribe]);

  const goHome = () => {
    setMode("gallery");
    scrollMainToTop("auto");
  };

  const handleTitleClick = () => {
    setMode("gallery");
    scrollMainToTop("auto");
  };

  const revealBase = cleanTxid(revealTxid || "");
  const mempoolUrl = revealBase ? `https://mempool.space/tx/${revealBase}` : "";

  // ── wallet status (max per wallet) – front-end helper ──
  const [walletStatus, setWalletStatus] = useState(null);
  useEffect(() => {
    if (!info || !info.address) {
      setWalletStatus(null);
      return;
    }
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`${ORCH_API}/wallet/${encodeURIComponent(info.address)}`);
        const j = await r.json();
        if (!alive) return;
        if (!r.ok) throw new Error(j.error || "wallet status failed");
        setWalletStatus(j);
      } catch {
        if (!alive) return;
        setWalletStatus(null);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [info && info.address]);

  const walletCapReached = walletStatus?.limit > 0 && walletStatus?.can_mint === false;
  const walletCapMsg = walletCapReached ? `max ${walletStatus.limit} per wallet` : "";

  // connect button toast when UniSat missing (auto-hide after 5s)
  const [connectToast, setConnectToast] = useState("");
  const connectToastTimerRef = useRef(null);

  const showConnectToast = (msg) => {
    setConnectToast(msg);
    if (connectToastTimerRef.current) {
      clearTimeout(connectToastTimerRef.current);
      connectToastTimerRef.current = null;
    }
    connectToastTimerRef.current = setTimeout(() => {
      setConnectToast("");
      connectToastTimerRef.current = null;
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (connectToastTimerRef.current) {
        clearTimeout(connectToastTimerRef.current);
      }
    };
  }, []);

  const handleConnectClick = async () => {
    if (typeof window !== "undefined" && !window.unisat) {
      showConnectToast("unisat wallet not found");
      return;
    }
    await ensureConnected();
  };

  // fee rate adjustment helpers (min 2)
  const adjustFeeRate = (delta) => {
    setFeeRate((prev) => {
      let n = Number(prev);
      if (!Number.isFinite(n)) n = 2;
      n += delta;
      if (n < 2) n = 2;
      if (n > 500) n = 500;
      return n;
    });
  };

  const handleFeeRateInput = (e) => {
    const v = e.target.value.trim();
    if (v === "") {
      setFeeRate("");
      return;
    }
    const n = Number(v);
    if (Number.isNaN(n)) return;
    const clamped = Math.max(2, Math.min(500, n));
    setFeeRate(clamped);
  };

  const signButtonLabel = (() => {
    if (busyStep === "sign") return "Signing…";
    if (busyStep === "broadcast") return "Broadcasting…";
    if (quoting) return "Quoting…";
    if (!quote) return "Prepare inscription";
    return "Sign & Submit";
  })();

  const signLabelSpanStyle =
    quoting && !busyStep ? { display: "inline-block", animation: "pulse 1s ease-in-out infinite" } : undefined;

  const reservationExpired = !revealTxid && formOpen && timerText && timerText.trim() === "00:00";
  const signDisabled = submitDisabled || walletCapReached;

  const unisatMissing = typeof window !== "undefined" && !window.unisat && !info?.address;

  /* CTA behavior */
  const inscribeCtaLabel = mintCompleteClicked ? "Mint Complete" : "Inscribe Bitcoin Knot";

  const handleInscribeCtaClick = () => {
    if (!PUBLIC_INSCRIBE_ENABLED) {
      // prevent any scroll jump in the gallery area when toggling the CTA label/state
      suppressGalleryScrollUntilRef.current = Date.now() + 750;

      const el = mainScrollRef.current;
      const prevTop = el ? el.scrollTop : null;

      setMintCompleteClicked(true);

      if (el && prevTop != null) {
        requestAnimationFrame(() => {
          try {
            if (mainScrollRef.current) mainScrollRef.current.scrollTop = prevTop;
          } catch {}
        });
      }
      return;
    }
    if (!canOpenInscribeForm) return;
    if (effectivePaused) return;
    scrollMainToTop("auto");
    startInscribe();
  };

  const mintCompleteCtaStyle = {
    ...styles.ctaLink,
    cursor: mintCompleteClicked ? "default" : "pointer",
    textDecoration: mintCompleteClicked ? "none" : "underline",
    opacity: 1,
    pointerEvents: mintCompleteClicked ? "none" : "auto",
  };

  /* paused style (kept) */
  const pausedCtaStyle = {
    ...styles.ctaLink,
    cursor: "default",
    opacity: 0.6,
    textDecoration: "none",
    pointerEvents: "none",
  };

  const renderCta = () => {
    if (!PUBLIC_INSCRIBE_ENABLED) {
      return (
        <button type="button" onClick={handleInscribeCtaClick} style={mintCompleteCtaStyle}>
          {inscribeCtaLabel}
        </button>
      );
    }

    if (effectivePaused || !canOpenInscribeForm) {
      return (
        <button type="button" disabled style={pausedCtaStyle} aria-disabled="true">
          Inscribe Bitcoin Knot (paused)
        </button>
      );
    }

    return (
      <button type="button" onClick={handleInscribeCtaClick} style={styles.ctaLink}>
        {inscribeCtaLabel}
      </button>
    );
  };

  // scroll-to-top on pagination
  const handleGalleryPageChange = () => {
    if (Date.now() < suppressGalleryScrollUntilRef.current) return;
    scrollMainToTop("auto");
  };

  // helper: header left block (handle in place of UniSat UI)
  const renderHeaderLeft = () => {
    if (!SHOW_WALLET_UI) {
      return <span style={styles.microLink}>@bitcoinknots</span>;
    }

    // keep old wallet UI intact behind toggle
    return !info ? (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <button onClick={handleConnectClick} disabled={walletBusy} style={styles.headerLink}>
          {walletBusy ? "…" : connectLabel}
        </button>
        {connectToast && <p style={{ ...styles.noteNeutral, marginTop: 6 }}>{connectToast}</p>}
      </div>
    ) : (
      <div style={walletRow}>
        <span style={{ fontSize: 14, fontWeight: 600, opacity: 0.9 }}>
          {info.address.slice(0, 4)}…{info.address.slice(-4)}
        </span>
        <button onClick={disconnect} style={styles.navXBtn} aria-label="Disconnect wallet" title="Disconnect">
          ×
        </button>
      </div>
    );
  };

  // ✅ When overlay mode is ON, add top padding so content can scroll behind navbar
  const mainScrollStyle = {
    ...styles.mainScroll,
    paddingTop: TRANSPARENT_NAV_AND_TOOLBAR ? navHeight : 0,
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerOuter} ref={headerRef}>
        {width >= 640 ? (
          <div style={styles.masthead}>
            <div style={styles.mastSide}>{renderHeaderLeft()}</div>

            <div style={styles.mastCenter}>
              <div style={styles.logoWrap}>
                <TitleText
                  styles={styles}
                  color={FORCE_ORANGE_TEXT ? ORANGE : theme.titleColor || "#111111"}
                  onClick={handleTitleClick}
                />
              </div>
            </div>

            <div style={styles.mastSideRight}>{renderCta()}</div>
          </div>
        ) : (
          <>
            <div style={styles.mastheadNarrowTop}>
              {renderHeaderLeft()}
              {renderCta()}
            </div>

            <div style={styles.mastLogoNarrow}>
              <div style={styles.logoWrap}>
                <TitleText
                  styles={styles}
                  color={FORCE_ORANGE_TEXT ? ORANGE : theme.titleColor || "#111111"}
                  onClick={handleTitleClick}
                />
              </div>
            </div>
          </>
        )}

        {/* ✅ about + index tabs removed (kept in code elsewhere if needed) */}
      </div>

      {/* ✅ attach ref so we can scroll this area back to top */}
      <div style={mainScrollStyle} ref={mainScrollRef}>
        {isGallery && (
          <div style={styles.sectionWrap}>
            <KnotsGalleryAdaptive
              styles={styles}
              showAllAssets={showAllAssets}
              theme={theme}
              sizeMode={sizeMode}
              smoothingMode={smoothingMode}
              previewMode={previewMode}
              onThemePresetChange={applyThemePreset}
              ORCH_API={ORCH_API}
              DL_ICON_SRC={DL_ICON_SRC}
              launchGalleryMode={launchGalleryMode}
              pageSize={ASSETS_PAGE_SIZE}
              assetsPerPage={ASSETS_PAGE_SIZE}
              onPageChange={handleGalleryPageChange}
            />
          </div>
        )}

        {!isGallery && (
          <div style={{ ...contentNarrow, ...inscribeCenterWrap }}>
            {noStock && (
              <div
                style={{
                  minHeight: FORM_AREA_MIN_HEIGHT,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 12,
                }}
              >
                <p style={styles.errLight} role="status">
                  all available knots in this phase are currently either reserved or minted, try again later.
                </p>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={handleRetry}
                    style={retrySec > 0 ? styles.btnSmallDisabled : styles.btnSmall}
                    disabled={retrySec > 0}
                  >
                    {retrySec > 0 ? `Try again (${retrySec}s)` : "Try again"}
                  </button>
                  <button onClick={goHome} style={styles.btnSmallGhost}>
                    Return to home
                  </button>
                </div>
              </div>
            )}

            {/* Hide mint form when noStock */}
            {isFormReady && !noStock && (
              <>
                {!revealTxid && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 26 }}>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Inscribe x1 Bitcoin Knot</h2>
                    <button title="Cancel" onClick={handleClose} style={styles.iconGhostXL} aria-label="Cancel">
                      ×
                    </button>
                  </div>
                )}

                <div style={{ minHeight: FORM_AREA_MIN_HEIGHT }}>
                  {!revealTxid && formOpen && (
                    <div style={{ marginTop: 22, paddingTop: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.9, marginTop: 2 }}>
                            Expires in {timerText}
                          </span>
                        </div>

                        {reservationExpired && (
                          <button
                            type="button"
                            onClick={startInscribe}
                            style={{ ...styles.headerLink, fontSize: 12, fontWeight: 600 }}
                          >
                            start new reservation
                          </button>
                        )}
                      </div>

                      {/* fee rate control */}
                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <span style={{ fontSize: 14, fontWeight: 600 }}>Fee rate</span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                borderBottom: `1px solid ${textMainColor}`,
                                paddingBottom: 2,
                              }}
                            >
                              <input
                                type="text"
                                value={feeRate}
                                onChange={handleFeeRateInput}
                                style={{
                                  width: 40,
                                  padding: "2px 2px 2px 0",
                                  background: "transparent",
                                  color: textMainColor,
                                  border: "none",
                                  outline: "none",
                                  fontSize: 14,
                                  fontWeight: 600,
                                }}
                              />
                              <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 4 }}>sat/vB</span>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <button
                                type="button"
                                onClick={() => adjustFeeRate(+1)}
                                style={{
                                  width: 14,
                                  height: 8,
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  fontSize: 8,
                                  lineHeight: "8px",
                                  padding: 0,
                                  color: textMainColor,
                                  opacity: 0.9,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.9")}
                                aria-label="Increase fee rate"
                              >
                                ▲
                              </button>

                              <button
                                type="button"
                                onClick={() => adjustFeeRate(-1)}
                                style={{
                                  width: 14,
                                  height: 8,
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  fontSize: 8,
                                  lineHeight: "8px",
                                  padding: 0,
                                  color: textMainColor,
                                  opacity: 0.9,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.9")}
                                aria-label="Decrease fee rate"
                              >
                                ▼
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* price + submit */}
                      <div style={{ minHeight: 220, marginTop: 14 }}>
                        {!quote ? (
                          <>
                            <StablePlaceholderPriceCard styles={styles} />
                            <div style={{ marginTop: 8, marginBottom: 4, textAlign: "left" }}>
                              <button
                                type="button"
                                onClick={refreshQuote}
                                disabled={quoting}
                                style={{
                                  ...styles.refreshLink,
                                  opacity: quoting ? 0.5 : 1,
                                  cursor: quoting ? "default" : "pointer",
                                }}
                              >
                                refresh
                              </button>
                            </div>

                            <button style={styles.btnPrimaryDisabled} disabled>
                              <span style={signLabelSpanStyle}>{signButtonLabel}</span>
                            </button>

                            {(quoteErr || actionErr) && <p style={styles.noteNeutral}>{quoteErr || actionErr}</p>}
                            {walletCapMsg && <p style={styles.noteNeutral}>{walletCapMsg}</p>}
                          </>
                        ) : (
                          <>
                            <PriceCard
                              mint={quote.platformFee ?? 0}
                              network={quote.fee || 0}
                              usdRate={usdRate}
                              styles={styles}
                            />

                            <div style={{ marginTop: 8, marginBottom: 4, textAlign: "left" }}>
                              <button
                                type="button"
                                onClick={refreshQuote}
                                disabled={quoting || busyStep === "sign" || busyStep === "broadcast"}
                                style={{
                                  ...styles.refreshLink,
                                  opacity:
                                    quoting || busyStep === "sign" || busyStep === "broadcast" ? 0.5 : 1,
                                  cursor:
                                    quoting || busyStep === "sign" || busyStep === "broadcast"
                                      ? "default"
                                      : "pointer",
                                }}
                              >
                                refresh
                              </button>
                            </div>

                            <button
                              onClick={!signDisabled ? handleInscribe : undefined}
                              style={signDisabled ? styles.btnPrimaryDisabled : styles.btnPrimary}
                              disabled={signDisabled}
                            >
                              <span style={signLabelSpanStyle}>{signButtonLabel}</span>
                            </button>

                            {(quoteErr || actionErr) && <p style={styles.noteNeutral}>{quoteErr || actionErr}</p>}
                            {walletCapMsg && <p style={styles.noteNeutral}>{walletCapMsg}</p>}
                          </>
                        )}

                        <HelpAccordionUnderSubmit styles={styles} />
                      </div>
                    </div>
                  )}

                  {/* success state */}
                  {revealTxid && !noStock && (
                    <div style={styles.successCard}>
                      <div style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 600 }}>
                        your bitcoin knot is inscribing
                      </div>

                      {mempoolUrl ? (
                        <p style={{ margin: "0 0 20px", wordBreak: "break-all", fontSize: 14, fontWeight: 600 }}>
                          <a
                            href={mempoolUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "inherit", textDecoration: "underline" }}
                          >
                            {mempoolUrl}
                          </a>
                        </p>
                      ) : (
                        <p style={{ margin: "0 0 20px", fontSize: 14, fontWeight: 600 }}>—</p>
                      )}

                      <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 10 }}>
                        <button onClick={goHome} style={{ ...styles.headerLink, fontSize: 14, fontWeight: 600 }}>
                          Return to home
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* loading / waiting state */}
            {!isFormReady && !noStock && (
              <div
                style={{
                  minHeight: FORM_AREA_MIN_HEIGHT,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 12,
                }}
              >
                {unisatMissing ? (
                  <p style={{ ...styles.noteNeutral, marginTop: 0 }}>
                    you must have unisat wallet installed to inscribe bitcoin knots.{" "}
                    <a
                      href="https://unisat.io/download"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "inherit", textDecoration: "underline" }}
                    >
                      install it here
                    </a>
                  </p>
                ) : (
                  <p style={{ ...styles.noteNeutral, marginTop: 0 }}>
                    {waitingForWallet
                      ? "Waiting for wallet connection…"
                      : knotsErr
                      ? String(knotsErr)
                      : "Preparing your inscription…"}
                  </p>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={startInscribe} style={styles.btnSmall}>
                    Retry
                  </button>
                  <button onClick={goHome} style={styles.btnSmallGhost}>
                    Return to home
                  </button>
                </div>

                {walletErr && !unisatMissing && <p style={styles.noteNeutral}>{walletErr}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom-left: S3 toggle (normal mode only) */}
      <div
        style={{ ...styles.cornerHotArea, left: 0, bottom: 0 }}
        onMouseEnter={() => setShowAltCorner(true)}
        onMouseLeave={() => setShowAltCorner(false)}
      >
        {showAltCorner && !previewMode && (
          <div style={{ position: "fixed", left: 10, bottom: 10, zIndex: 9998 }}>
            <button
              onClick={() => setShowAllAssets((v) => !v)}
              style={styles.altToggleBtn}
              title="Toggle S3 assets in gallery"
            >
              {showAllAssets ? "Show inscribed only" : "Show all assets"}
            </button>
          </div>
        )}
      </div>

      {/* Bottom-right: theme panel (preview mode only, disabled via ENABLE_TUNER) */}
      {previewMode && ENABLE_TUNER && (
        <>
          <div
            style={{ ...styles.cornerHotArea, right: 0, bottom: 0 }}
            onMouseEnter={() => setShowTuneCorner(true)}
            onMouseLeave={() => setShowTuneCorner(false)}
          >
            {showTuneCorner && (
              <div style={{ position: "fixed", right: 10, bottom: 10, zIndex: 9999 }}>
                <button onClick={() => setTunerOpen((v) => !v)} title="Theme" style={styles.tunerBtn}>
                  +
                </button>
              </div>
            )}
          </div>

          {tunerOpen && (
            <ThemeTunerPanel
              theme={theme}
              setTheme={setTheme}
              styles={styles}
              smoothingMode={smoothingMode}
              setSmoothingMode={setSmoothingMode}
              sizeMode={sizeMode}
              setSizeMode={setSizeMode}
              onClose={() => setTunerOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

/* Theme tuner (unchanged, kept for future) */
function ThemeTunerPanel({ theme, setTheme, styles, smoothingMode, setSmoothingMode, sizeMode, setSizeMode, onClose }) {
  const set = (k, v) => setTheme((prev) => ({ ...prev, [k]: v }));

  const presets = {
    White: {
      mode: "white",
      bg: "#FFFFFF",
      textA: "#111111",
      textB: "#111111",
      cardText: "#111111",
      button: "#111111",
      buttonText: "#FFFFFF",
      strokeColor: "#E6E6E6",
      strokeWeight: 0,
      showSubtext: false,
      titleColor: "#111111",
      titleScale: 0.66,
      cta: "#FF3300",
      ctaInHeader: true,
    },
    Orange: {
      mode: "orange",
      bg: "#FF3300",
      textA: "#111111",
      textB: "#111111",
      cardText: "#111111",
      button: "#111111",
      buttonText: "#FFFFFF",
      strokeColor: "#E6E6E6",
      strokeWeight: 0,
      showSubtext: false,
      titleColor: "#111111",
      titleScale: 0.66,
      cta: "#FFFFFF",
      ctaInHeader: true,
    },
  };

  return (
    <div style={styles.tunerPanel}>
      <div style={styles.tunerRow}>
        <span style={styles.tunerLabel}>Background</span>
        <input
          type="color"
          value={theme.bg}
          onChange={(e) => set("bg", e.target.value)}
          style={styles.tunerInputColor}
        />
      </div>

      <div style={styles.tunerRow2}>
        <span style={styles.tunerLabel}>Text A / B</span>
        <input
          type="color"
          value={theme.textA}
          onChange={(e) => set("textA", e.target.value)}
          style={styles.tunerInputColor}
        />
        <input
          type="color"
          value={theme.textB}
          onChange={(e) => set("textB", e.target.value)}
          style={styles.tunerInputColor}
        />
      </div>

      <div style={styles.tunerRow}>
        <span style={styles.tunerLabel}>Card Text</span>
        <input
          type="color"
          value={theme.cardText}
          onChange={(e) => set("cardText", e.target.value)}
          style={styles.tunerInputColor}
        />
      </div>

      <div style={styles.tunerRow2}>
        <span style={styles.tunerLabel}>Button / Text</span>
        <input
          type="color"
          value={theme.button}
          onChange={(e) => set("button", e.target.value)}
          style={styles.tunerInputColor}
        />
        <input
          type="color"
          value={theme.buttonText}
          onChange={(e) => set("buttonText", e.target.value)}
          style={styles.tunerInputColor}
        />
      </div>

      <div style={styles.tunerRow}>
        <span style={styles.tunerLabel}>CTA Link</span>
        <input
          type="color"
          value={theme.cta}
          onChange={(e) => set("cta", e.target.value)}
          style={styles.tunerInputColor}
        />
      </div>

      <div style={styles.tunerRow}>
        <span style={styles.tunerLabel}>Smoothing</span>
        <select
          value={smoothingMode}
          onChange={(e) => setSmoothingMode(e.target.value)}
          style={{ width: "100%", height: 22, fontSize: 11 }}
        >
          <option value="auto">auto (xs/S smooth, L pixel, lightbox pixel)</option>
          <option value="smooth">smooth</option>
          <option value="pixelated">pixelated</option>
        </select>
      </div>

      <div style={styles.tunerRow}>
        <span style={styles.tunerLabel}>Thumbnail size</span>
        <select
          value={sizeMode}
          onChange={(e) => setSizeMode(e.target.value)}
          style={{ width: "100%", height: 22, fontSize: 11 }}
        >
          <option value="small">xs</option>
          <option value="large">S</option>
          <option value="xl">L</option>
        </select>
      </div>

      <div style={styles.tunerRow2}>
        <span style={styles.tunerLabel}>Stroke</span>
        <input
          type="number"
          min={0}
          max={12}
          step={1}
          value={theme.strokeWeight}
          onChange={(e) => set("strokeWeight", Math.max(0, Math.min(12, Number(e.target.value) || 0)))}
          style={styles.tunerInputNum}
        />
        <input
          type="color"
          value={theme.strokeColor}
          onChange={(e) => set("strokeColor", e.target.value)}
          style={styles.tunerInputColor}
        />
      </div>

      <div style={styles.tunerRow2}>
        <span style={styles.tunerLabel}>Title color / scale</span>
        <input
          type="color"
          value={theme.titleColor}
          onChange={(e) => set("titleColor", e.target.value)}
          style={styles.tunerInputColor}
        />
        <input
          type="number"
          min={0.2}
          max={1.5}
          step={0.02}
          value={theme.titleScale}
          onChange={(e) => set("titleScale", Math.max(0.2, Math.min(1.5, Number(e.target.value) || 0.66)))}
          style={styles.tunerInputNum}
        />
      </div>

      <div style={styles.tunerRow}>
        <span style={styles.tunerLabel}>Show subtext</span>
        <input
          type="checkbox"
          checked={!!theme.showSubtext}
          onChange={(e) => set("showSubtext", e.target.checked)}
          style={{ width: 16, height: 16 }}
        />
      </div>

      <div style={styles.tunerRow}>
        <span style={styles.tunerLabel}>CTA in header</span>
        <input
          type="checkbox"
          checked={!!theme.ctaInHeader}
          onChange={(e) => set("ctaInHeader", e.target.checked)}
          style={{ width: 16, height: 16 }}
        />
      </div>

      <div style={styles.tunerFooter}>
        <button style={styles.tunerLink} onClick={() => setTheme(presets.Orange)}>
          o theme
        </button>
        <button style={styles.tunerLink} onClick={() => setTheme(presets.White)}>
          w theme
        </button>
        <button style={styles.tunerLink} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}
